<template><div><code-well-header>
  <example-popover modal />
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A Popover contains a dialog that will appear above other content when activated. It will always appear in a location relative to the anchor.
If you are looking for a dialog that does not display relative to the anchor, see <RouteLink to="/components/modal.html" class="d-docsite--link d-link">Modal</RouteLink>.
Some common examples of popover usage: dropdown list, emoji picker dialog, add comment dialog.
A popover can be modal or non-modal. Below are some guidelines on when to use a modal vs non-modal popover.</p>
<p class="d-docsite--paragraph">Your popover should be modal when:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">It contains scrollable content.</li>
<li class="d-docsite--list-element">It contains components that hold user input state (input, checkbox).</li>
</ul>
<p class="d-docsite--paragraph">Your popover should be non-modal when:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">It is not scrollable.</li>
<li class="d-docsite--list-element">It contains only components that do not hold state (link, button).</li>
</ul>
<p class="d-docsite--paragraph">The content slot will be rendered lazily when the popover is open. By default, the popover content will be opened when the anchor is clicked, and closed when clicking outside the content or on <code v-pre>ESC</code> key press. You may override this behaviour by using <code v-pre>.sync</code> on the open prop (or <code v-pre>v-model:open</code> in Vue 3) in which you can open or close the content using whichever condition you wish.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Smaller sized dialogs that trigger on user activation of an anchor element.</li>
<li class="d-docsite--list-element">Dialogs that should be positioned relative to the anchor.</li>
<li class="d-docsite--list-element">Dialogs that contain interactive components.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Content that is displayed on hover. Instead, use a <RouteLink to="/components/tooltip.html" class="d-docsite--link d-link">Tooltip</RouteLink>.</li>
<li class="d-docsite--list-element">Dialogs that should be positioned in the center of the screen.</li>
<li class="d-docsite--list-element">Dialogs that are very large.</li>
<li class="d-docsite--list-element">Alerts.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Popovers should be fairly small. If you are looking for more of a full size dialog solution see <RouteLink to="/components/modal.html" class="d-docsite--link d-link">Modal</RouteLink></li>
<li class="d-docsite--list-element">Trigger using an anchor element, such as a button.</li>
<li class="d-docsite--list-element">Render the dialog at the body element.</li>
<li class="d-docsite--list-element">Focus the first interactive element within the dialog after it is opened.</li>
<li class="d-docsite--list-element">Close the dialog when ESC is pressed.</li>
<li class="d-docsite--list-element">Close non-modal dialogs if they are scrolled out of visibility.</li>
<li class="d-docsite--list-element">Set the z-index of the dialog to var(--zi-modal-element) if modal, var(--zi-popover) if not.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="popover-modal" tabindex="-1" class="d-docsite--header-3">Popover - Modal <a class="header-anchor d-link d-docsite--link d-link" href="#popover-modal" aria-hidden="true">#</a></h3>
<code-well-header>
  <example-popover modal />
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-popover">
  <div id="DtPopover__anchor1">
    <button type="button" class="base-button__button d-btn d-btn--primary" aria-expanded="false">
      <span class="d-btn__label base-button__label"> View Popover </span>
    </button>
  </div>
</div>
<div class="tippy-box d-ps-absolute" id="tippy-1" style="z-index: 650; position: absolute; inset: 0px 0px auto auto; margin: 0px; transform: translate3d(-593px, 197px, 0px);" data-popper-placement="bottom-end">
  <div id="dt0" role="dialog" aria-hidden="false" aria-labelledby="DtPopover__anchor1" aria-modal="false" tabindex="-1" class="d-popover__dialog d-popover__dialog--modal" style="">
    <div class="d-popover__content d-p16">
      <div>
        <p class="d-mb4">This is content rendered within the popover.</p>
        <button type="button" class="base-button__button d-btn d-btn--primary">
          <span class="d-btn__label base-button__label"> Button </span>
        </button>
      </div>
    </div>
  </div>
</div>
'
vueCode='
<dt-popover
  :open="onOpen"
>
  <template #anchor>
    <dt-button>
       View Popover
    </dt-button>
  </template>
  <template
    #content="{ close }"
  >
    <div>
      <p class="d-mb4">
        This is content rendered within the popover.
      </p>
      <dt-button
        @click="close"
      >
        Button
      </dt-button>
    </div>
  </template>
</dt-popover>
'
showHtmlWarning />
<h3 id="popover-non-modal" tabindex="-1" class="d-docsite--header-3">Popover - Non Modal <a class="header-anchor d-link d-docsite--link d-link" href="#popover-non-modal" aria-hidden="true">#</a></h3>
<code-well-header>
  <example-popover />
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-popover">
  <div id="DtPopover__anchor1">
    <button type="button" class="base-button__button d-btn d-btn--primary" aria-expanded="false">
      <span class="d-btn__label base-button__label"> View Popover </span>
    </button>
  </div>
</div>
<div class="tippy-box d-ps-absolute" data-tippy-root="" id="tippy-1" data-popper-placement="bottom-end" style="z-index: 300; position: absolute; inset: 0px 0px auto auto; margin: 0px; transform: translate3d(-593px, 197px, 0px);">
  <div id="dt0" role="dialog" aria-hidden="false" aria-labelledby="DtPopover__anchor1" aria-modal="true" tabindex="-1" class="d-popover__dialog" style="">
    <div class="d-popover__content d-p16">
      <div>
        <p class="d-mb4">This is content rendered within the popover.</p>
        <button type="button" class="base-button__button d-btn d-btn--primary">
          <span class="d-btn__label base-button__label"> Button </span>
        </button>
      </div>
    </div>
  </div>
</div>
'
vueCode='
<dt-popover
  :open="onOpen"
  :modal="false"
>
  <template #anchor>
    <dt-button>
      View Popover
    </dt-button>
  </template>
  <template
    #content="{ close }"
  >
    <div>
      <p class="d-mb4">
        This is content rendered within the popover.
      </p>
      <dt-button
        @click="close"
      >
        Button
      </dt-button>
    </div>
  </template>
</dt-popover>
'
showHtmlWarning />
<h3 id="with-header-modal" tabindex="-1" class="d-docsite--header-3">With Header - Modal <a class="header-anchor d-link d-docsite--link d-link" href="#with-header-modal" aria-hidden="true">#</a></h3>
<code-well-header>
  <example-popover modal header>
    <template #content>
      <div class="d-mb8">Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae.<br></div>
    </template>
  </example-popover>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-popover">
  <div id="DtPopover__anchor1">
    <button type="button" class="base-button__button d-btn d-btn--primary" aria-expanded="false">
      <span class="d-btn__label base-button__label"> View Popover </span>
    </button>
  </div>
</div>
<div class="tippy-box d-ps-absolute" data-tippy-root="" id="tippy-1" data-popper-placement="bottom-end" style="z-index: 650; position: absolute; inset: 0px 0px auto auto; margin: 0px; transform: translate3d(5px, 197px, 0px);">
  <div
    id="dt0"
    role="dialog"
    aria-hidden="false"
    aria-labelledby="DtPopover__anchor1"
    aria-modal="false"
    tabindex="-1"
    class="d-popover__dialog d-popover__dialog--modal"
    style="max-height: calc(100vh - var(--dt-space-300));"
  >
    <div class="d-popover__header d-pl16">
      <div class="d-popover__header__content"><div class="d-w100p">This is the header</div></div>
    </div>
    <div class="d-popover__content d-p16">
      <div>
        <div class="d-mb8">
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam
          repudiandae. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis
          obcaecati quibusdam repudiandae.<br />
        </div>
        <button type="button" class="base-button__button d-btn d-btn--primary">
          <span class="d-btn__label base-button__label"> Button </span>
        </button>
      </div>
    </div>
  </div>
</div>
'
vueCode='
<dt-popover
  :open="onOpen"
>
  <template #anchor>
    <dt-button>
      View Popover
    </dt-button>
  </template>
  <template #headerContent>
    <div class="d-w100p">
      This is the header
    </div>
  </template>
  <template
    #content="{ close }"
  >
    <div>
      <div class="d-mb8">
        Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae.<br>
      </div>
      <dt-button
        @click="close"
      >
        Button
      </dt-button>
    </div>
  </template>
</dt-popover>
'
showHtmlWarning />
<h3 id="with-footer-modal" tabindex="-1" class="d-docsite--header-3">With Footer - Modal <a class="header-anchor d-link d-docsite--link d-link" href="#with-footer-modal" aria-hidden="true">#</a></h3>
<code-well-header>
  <example-popover modal footer>
    <template #content>
      <div class="d-mb8">Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae.<br></div>
    </template>
  </example-popover>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-popover">
  <div id="DtPopover__anchor1">
    <button type="button" class="base-button__button d-btn d-btn--primary" aria-expanded="false">
      <span class="d-btn__label base-button__label"> View Popover </span>
    </button>
  </div>
</div>
<div class="tippy-box d-ps-absolute" data-tippy-root="" id="tippy-5" style="z-index: 650; position: absolute; inset: 0px 0px auto auto; margin: 0px; transform: translate3d(5px, 197px, 0px);" data-popper-placement="bottom-end">
  <div
    id="dt0"
    role="dialog"
    aria-hidden="false"
    aria-labelledby="DtPopover__anchor1"
    aria-modal="false"
    tabindex="-1"
    class="d-popover__dialog d-popover__dialog--modal"
    style="max-height: calc(100vh - var(--dt-space-300));"
  >
    <div class="d-popover__content d-p16">
      <div>
        <div class="d-mb8">
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam
          repudiandae. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis
          obcaecati quibusdam repudiandae.<br />
        </div>
        <button type="button" class="base-button__button d-btn d-btn--primary">
          <span class="d-btn__label base-button__label"> Button </span>
        </button>
      </div>
    </div>
    <div class="d-popover__footer d-pl16">
      <div class="d-popover__footer__content"><div class="d-w100p">This is the footer</div></div>
    </div>
  </div>
</div>
'
vueCode='
<dt-popover
  :open="onOpen"
>
  <template #anchor>
    <dt-button>
      View Popover
    </dt-button>
  </template>
  <template
    #content="{ close }"
  >
    <div>
      <div class="d-mb8">
        Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Consequuntur delectus distinctio id iure labore, maiores mollitia reprehenderit sunt tempore veritatis. Aliquam delectus earum ex, expedita ipsam nobis obcaecati quibusdam repudiandae.<br>
      </div>
      <dt-button
        @click="close"
      >
        Button
      </dt-button>
    </div>
  </template>
  <template #footerContent>
    <div class="d-w100p">
      This is the footer
    </div>
  </template>
</dt-popover>
'
showHtmlWarning />
<h3 id="fallback-placements" tabindex="-1" class="d-docsite--header-3">Fallback Placements <a class="header-anchor d-link d-docsite--link d-link" href="#fallback-placements" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The popover uses <a href="https://atomiks.github.io/tippyjs/v6/headless-tippy/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">headless-tippy</a> and
<a href="https://popper.js.org/docs/v2/modifiers/flip/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">popper</a>, if the popover opens in a placement where it will
be clipped, it will move to a new position. It will do this automatically by default, but if you want to
manually specify which position it will move to in what order you can do so via the <code v-pre>fallbackPlacements</code> prop.</p>
<code-well-header>
  <example-popover :fallback-placements="['top']" button-text="Fallback placement: top" />
</code-well-header>
<code-example-tabs
vueCode='
<dt-popover
  :open="onOpen"
  :fallback-placements="[`top`]"
>
  <template #anchor>
    <dt-button>
       fallback placement: top
    </dt-button>
  </template>
  <template
    #content="{ close }"
  >
    <div>
      <p class="d-mb4">
        This is content rendered within the popover.
      </p>
      <dt-button
        @click="close"
      >
        Button
      </dt-button>
    </div>
  </template>
</dt-popover>
'
/>
<h3 id="padding" tabindex="-1" class="d-docsite--header-3">Padding <a class="header-anchor d-link d-docsite--link d-link" href="#padding" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Padding options for the popover content are provided via size classes &quot;small&quot;, &quot;medium&quot; or &quot;large&quot; in order to standardize the look of the popover content between usages. To remove the padding from the content, you can pass &quot;none&quot;. Setting none will also allow you to set custom padding via utility classes (Ex: you only want padding on the left.).</p>
<code-well-header>
  <example-popover padding="small" />
</code-well-header>
<code-example-tabs
vueCode='
<dt-popover
  :open="onOpen"
  padding="small"
>
  <template #anchor>
    <dt-button>
       View Popover
    </dt-button>
  </template>
  <template
    #content="{ close }"
  >
    <div>
      <p class="d-mb4">
        This is content rendered within the popover.
      </p>
      <dt-button
        @click="close"
      >
        Button
      </dt-button>
    </div>
  </template>
</dt-popover>
'
/>
<h3 id="force-close-all-opened-instances" tabindex="-1" class="d-docsite--header-3">Force Close All Opened Instances <a class="header-anchor d-link d-docsite--link d-link" href="#force-close-all-opened-instances" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When the popover is open, it will attach an event listener into the window object, so you can close the instances dispatching the <code v-pre>dt-popover-close</code> event in the window object:</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">const</span> e <span class="token operator">=</span> <span class="token keyword">new</span> <span class="token class-name">Event</span><span class="token punctuation">(</span><span class="token string">'dt-popover-close'</span><span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">window<span class="token punctuation">.</span><span class="token function">dispatchEvent</span><span class="token punctuation">(</span>e<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="popover" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Popover must contain an anchor and content element. d-modal--transparent can be used as a sibling before the popover container if you wish to make the popover modal.</p>
<component-class-table component-name="popover" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">If your popover is modal, please see the accessibility section of this page regarding &quot;focus trapping&quot;: <RouteLink to="/components/modal.html#accessibility" class="d-docsite--link d-link">Modal Accessibility</RouteLink>. The same rules will apply here if your popover is modal.</p>
<p class="d-docsite--paragraph">Popovers, in their current implementation, are accessible when used as interactive components. Content will be read to screen reader users, and the popover markup by is appended to the <code v-pre>&lt;body&gt;</code>.</p>
<p class="d-docsite--paragraph">There are a few important considerations to ensure popover controls are accessible:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The popover content will have a generic role of &quot;dialog&quot; ( &quot;menu&quot; and &quot;listbox&quot; are also possible roles as well).</li>
<li class="d-docsite--list-element">On open, focus will be transferred to the first focusable element within the popover, after close the triggering element will be focused.</li>
<li class="d-docsite--list-element">A screen reader visible only close button is added by default when setting the <code v-pre>showCloseButton</code> prop to <code v-pre>false</code>.</li>
</ul>
<component-accessible-table component-name="popover"/><h3 id="anchor" tabindex="-1" class="d-docsite--header-3">Anchor <a class="header-anchor d-link d-docsite--link d-link" href="#anchor" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The anchor element that activates the popover should be fully accessible by keyboard. The easiest way to do this is by using an element like an <code v-pre>DtButton</code> that is already accessible. The user should also be able to close the popover content using the <code v-pre>ESC</code> key for most ARIA roles.</p>
<p class="d-docsite--paragraph">There are some required ARIA attributes for the anchor element (such as <code v-pre>aria-expanded</code> set based on <code v-pre>open</code> and <code v-pre>aria-haspopup</code> that matches the <code v-pre>role</code>). To make this as straightforward as possible, these ARIA attributes are passed with the correct values as the <code v-pre>attrs</code> slot-scope to the anchor slot. Applying them is as simple as using <code v-pre>v-bind</code>:</p>
<div class="language-vue" data-highlighter="prismjs" data-ext="vue"><pre v-pre><code class="language-vue"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>template</span> <span class="token attr-name">#anchor</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>{ attrs }<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>dt-button</span> <span class="token attr-name">v-bind</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>attrs<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>I'm accessible now!<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>dt-button</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>template</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">By default, the dialog content will be labeled by the entire anchor element. To change this, you can do one of 2 things:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Pass <code v-pre>aria-label</code>, which is the text label that will be applied to the dialog content.</li>
<li class="d-docsite--list-element">Pass <code v-pre>aria-labelledby</code>, which is an ID of the element that should be used as the descriptive label.</li>
</ul>
<h3 id="keyboard-support" tabindex="-1" class="d-docsite--header-3">Keyboard Support <a class="header-anchor d-link d-docsite--link d-link" href="#keyboard-support" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The below keyboard functionality is automatically implemented when using the popover component:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The user can dismiss the popover pressing the <code v-pre>ESC</code> key, after that the focus will be returned to the element that launched it.</li>
<li class="d-docsite--list-element">The user can traverse focusable elements using the <code v-pre>TAB</code> key. If the popover has a defined header, the focus will be moved to the header buttons after the last focusable element inside content's container.</li>
</ul>
<p class="d-docsite--paragraph">Additionally you must use the &quot;initialFocusElement&quot; prop to set which element is initially focused when the popover opens. You can set this to &quot;first&quot; to focus the first focusable element, &quot;dialog&quot; to focus the dialog itself, a string starting with '#' to focus an element by id within the dialog or you may pass in an HTMLElement directly. If set to &quot;none&quot; the focus will remain on the anchor, however this is invalid behavior if the popover is modal.</p>
<h2 id="references" tabindex="-1" class="d-docsite--header-2">References <a class="header-anchor d-link d-docsite--link d-link" href="#references" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="https://atomiks.github.io/tippyjs/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">tippyjs</a></li>
<li class="d-docsite--list-element"><a href="https://popper.js.org/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">popper.js</a></li>
<li class="d-docsite--list-element"><a href="https://developer.apple.com/design/human-interface-guidelines/ios/views/popovers/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Apple. Human Interface Guidelines</a></li>
<li class="d-docsite--list-element"><a href="https://react-spectrum.adobe.com/react-aria/useOverlayTrigger.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Spectrum. Accessibility overlay trigger</a></li>
<li class="d-docsite--list-element"><a href="https://slack.design/articles/accessibility-a-powerful-design-tool/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Slack Design. Accessibility, a powerful design tool</a></li>
</ul>
</div></template>


<script setup>
  import ExamplePopover from '@exampleComponents/ExamplePopover.vue';
</script>